import React, { useEffect, useMemo, useRef, useState } from "react";
import styles from "./WWGStorePage.module.css";
import { useHistory, useLocation } from "react-router-dom";

import Navbar from "../../components/Navbar";
import BottomNav from "../../components/BottomNav";
import FilterMenu from "../../components/FilterMenu";
import StockList from "../../components/StockList";
import { useStockItems } from "../../hooks/useStockItems";
import { useCart, getCartItemCount } from "../../hooks/useCart";
import { useListItems, useLists } from "../../hooks/useLists";
import { useIsFreeTrial } from "../../hooks/useUser";
import TrialNotice from "../../components/TrialNotice";
import { buildCategoriesFromAPI } from "../../components/FilterButton/FilterButton";
import {
  parseStoreFilterParams,
  buildStoreFilterSearch,
  reconcileCategoryParams,
} from "./helpers/storeFilterParams";
import {
  trackProductsSearched,
  trackProductListFiltered,
} from "../../utils/analytics";

const sortOptions = [
  { id: 1, label: "Date Added: Newest", value: "newest" },
  { id: 2, label: "Date Added: Oldest", value: "oldest" },
  { id: 3, label: "Name: A To Z", value: "az" },
  { id: 4, label: "Name: Z To A", value: "za" },
];

const listOptions = [
  { id: 1, label: "List", value: "list" },
  { id: 2, label: "Column", value: "column" },
];

export function WWGStorePage() {
  const history = useHistory();
  const location = useLocation();

  const { data: stockData = {}, isLoading } = useStockItems();
  const { categories = [], stockItems = [] } = stockData;
  const { data: userLists = [] } = useLists();
  const { data: cartData } = useCart();
  const cartCount = getCartItemCount(cartData);
  const isFreeTrial = useIsFreeTrial();

  const [activeTab, setActiveTab] = useState("WWG Store");
  const initialFilterParams = useMemo(
    () => parseStoreFilterParams(location.search),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [isList, setIsList] = useState(
    () => initialFilterParams.layout !== "column"
  );
  const [sort, setSort] = useState(() => initialFilterParams.sort);
  const [activeCategory, setActiveCategory] = useState(
    () => initialFilterParams.category
  );
  const [activeSubCategory, setActiveSubCategory] = useState(
    () => initialFilterParams.subCategory
  );
  const [searchTerm, setSearchTerm] = useState(
    () => initialFilterParams.searchTerm
  );

  const categoryTree = useMemo(
    () => buildCategoriesFromAPI(categories),
    [categories]
  );

  const listScrollRef = useRef(null);

  const booksReadList = useMemo(
    () =>
      userLists.find(
        (list) =>
          String(list?.type || "").toUpperCase() === "BOOKS_READ" ||
          String(list?.name || "")
            .trim()
            .toLowerCase() === "books read"
      ) || null,
    [userLists]
  );
  const { data: booksReadItems = [] } = useListItems(booksReadList?.listId);

  const booksReadStockIds = useMemo(
    () =>
      new Set(
        booksReadItems.map((item) =>
          String(item?.stockId ?? item?.productId ?? item?.stockItemId ?? "")
        )
      ),
    [booksReadItems]
  );

  const sortedItems = useMemo(() => {
    const items = [...stockItems];
    if (sort === "az")
      return items.sort((a, b) => a.title.localeCompare(b.title));
    if (sort === "za")
      return items.sort((a, b) => b.title.localeCompare(a.title));
    if (sort === "newest")
      return items.sort(
        (a, b) => new Date(b.createdOn) - new Date(a.createdOn)
      );
    if (sort === "oldest")
      return items.sort(
        (a, b) => new Date(a.createdOn) - new Date(b.createdOn)
      );
    return items;
  }, [stockItems, sort]);

  const filteredItems = useMemo(() => {
    let items = sortedItems;

    const normalizedSearchTerm = String(searchTerm || "")
      .trim()
      .toLowerCase();
    if (normalizedSearchTerm) {
      items = items.filter((item) => {
        const haystack = [
          item?.title,
          item?.author,
          item?.creator,
          item?.sku,
          item?.category,
          item?.description,
          item?.note,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");
        return haystack.includes(normalizedSearchTerm);
      });
    }

    // Filter by category/subcategory if selected
    // If activeSubCategory is set, filter by that; otherwise filter by activeCategory
    // Read/Unread are the API's own sub-sub-category titles (Literature > Books >
    // Read/Unread) but represent a per-household read status rather than a static
    // category tag on the stock item, so they're excluded here and handled below
    // against booksReadStockIds instead.
    if (
      activeSubCategory &&
      activeSubCategory !== "Read" &&
      activeSubCategory !== "Unread"
    ) {
      // Filter by subcategory (e.g., Books, Info, Business, Kids, Tumblers, etc.)
      items = items.filter((item) => {
        const categoryTags = Array.isArray(item?.categoryTags)
          ? item.categoryTags
          : [];
        return categoryTags.some(
          (tag) => String(tag?.title || "").trim() === activeSubCategory
        );
      });
    } else if (activeCategory && activeCategory !== "Favorites") {
      // Filter by parent category (e.g., Literature, Clothing, Drinkware, etc.)
      // Also applies when Read/Unread is selected, narrowing down to the
      // parent category (e.g. Literature) before the read-status filter below.
      items = items.filter((item) => {
        const categoryTags = Array.isArray(item?.categoryTags)
          ? item.categoryTags
          : [];
        return categoryTags.some(
          (tag) => String(tag?.title || "").trim() === activeCategory
        );
      });
    }

    if (activeCategory === "Favorites") {
      items = items.filter((item) => Boolean(item?.isLiked));
    }

    // booksReadStockIds already comes from the BOOKS_READ list fetched above
    // (same mechanism ItemIconSection uses to mark items as read), so no
    // additional API call is needed here.
    if (activeSubCategory === "Read") {
      return items.filter((item) =>
        booksReadStockIds.has(
          String(item?.stockId ?? item?.productId ?? item?.stockItemId ?? "")
        )
      );
    }

    if (activeSubCategory === "Unread") {
      return items.filter(
        (item) =>
          !booksReadStockIds.has(
            String(item?.stockId ?? item?.productId ?? item?.stockItemId ?? "")
          )
      );
    }

    return items;
  }, [
    sortedItems,
    activeCategory,
    activeSubCategory,
    booksReadStockIds,
    searchTerm,
  ]);

  const handleTabClick = (item) => {
    setActiveTab(item.label);
    history.push(item.href);
  };

  const handleSort = (option) => setSort(option);
  const handleIsList = (option) => setIsList(option === "list");
  const handleCategoryChange = (category) => setActiveCategory(category);
  const handleSubCategoryChange = (subCategory) =>
    setActiveSubCategory(subCategory);
  const handleSearchInput = (term) => setSearchTerm(term);

  // Categories load asynchronously; once loaded, validate the category/
  // subCategory carried over from the URL against the real category tree
  // and self-correct if either no longer exists.
  const hasReconciledCategory = useRef(false);
  useEffect(() => {
    if (hasReconciledCategory.current) return;
    if (isLoading) return;
    // buildCategoriesFromAPI always yields a `Favorites` entry, so only treat
    // the tree as usable once it also contains real API-provided categories.
    // Reconciling against a Favorites-only tree would wipe a valid selection.
    if (activeCategory !== "Favorites" && categories.length === 0) return;

    hasReconciledCategory.current = true;

    if (!activeCategory) return;

    const reconciled = reconcileCategoryParams(
      { category: activeCategory, subCategory: activeSubCategory },
      categoryTree
    );

    if (reconciled.changed) {
      setActiveCategory(reconciled.category);
      setActiveSubCategory(reconciled.subCategory);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, categoryTree, categories]);

  // Single source of truth for URL sync: writes the current filter/sort/
  // layout state into the query string whenever any of it changes. Driving
  // this from committed state (rather than writing imperatively inside each
  // handler) avoids stale-closure races when a single user action triggers
  // multiple state updates in one tick (e.g. FilterButton clearing
  // subCategory when a new top-level category is picked).
  const isFirstQuerySync = useRef(true);
  useEffect(() => {
    if (isFirstQuerySync.current) {
      isFirstQuerySync.current = false;
      return;
    }

    const search = buildStoreFilterSearch({
      sort,
      layout: isList ? "list" : "column",
      category: activeCategory,
      subCategory: activeSubCategory,
      searchTerm,
    });
    history.replace({ pathname: location.pathname, search });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort, isList, activeCategory, activeSubCategory, searchTerm]);

  // Changing any filter setting should snap the item list back to the top,
  // otherwise the user can be left scrolled deep into a list that no longer
  // matches the new filter.
  const isFirstScrollReset = useRef(true);
  useEffect(() => {
    if (isFirstScrollReset.current) {
      isFirstScrollReset.current = false;
      return;
    }
    listScrollRef.current?.scrollTo({ top: 0 });
  }, [sort, activeCategory, activeSubCategory, searchTerm]);

  // Debounced so a full search term is tracked once the user pauses typing,
  // instead of firing one "Products Searched" event per keystroke.
  useEffect(() => {
    if (!searchTerm) return undefined;
    const timeoutId = setTimeout(() => {
      trackProductsSearched(searchTerm);
    }, 600);
    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  useEffect(() => {
    if (!activeCategory && !activeSubCategory) return;
    trackProductListFiltered({
      category: activeCategory,
      subCategory: activeSubCategory,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, activeSubCategory]);

  return (
    <div className={styles.container}>
      <div className={styles.desktopNav}>
        <Navbar
          onClick={() => {}}
          isHome={true}
          menuItems={[
            { label: "WWG Store", href: "/wwg-store" },
            { label: "My Lists", href: "/my-lists" },
            { label: "My Orders", href: "/my-orders" },
            { label: "Cart", href: "/cart" },
          ]}
          activeTab={activeTab}
          onTabClick={handleTabClick}
          cartCount={cartCount}
        />
      </div>

      {isFreeTrial && (
        <div className={styles.trialNotice}>
          <TrialNotice />
        </div>
      )}

      <div className={styles.filterMenu}>
        <FilterMenu
          dropdownOne={listOptions}
          dropdownTwo={sortOptions}
          selectedOneValue={isList ? "list" : "column"}
          selectedTwoValue={sort}
          handlePickOne={handleIsList}
          handlePickTwo={handleSort}
          onCategoryChange={handleCategoryChange}
          onSubCategoryChange={handleSubCategoryChange}
          onSearchInput={handleSearchInput}
          apiCategories={categories}
          initialCategory={activeCategory}
          initialSubCategory={activeSubCategory}
          initialSearchTerm={searchTerm}
        />
      </div>

      <div className={styles.listClip}>
        <div className={styles.listSpacer} />
        <div className={styles.list} ref={listScrollRef}>
          {filteredItems.length === 0 && !isLoading ? (
            <div className={styles.noResults}>
              No items matching filter terms
            </div>
          ) : (
            <StockList
              stockInfo={filteredItems}
              pageFrom={activeTab}
              isList={isList}
              isLoading={isLoading}
            />
          )}
        </div>
      </div>

      {/* Mobile-only bottom nav */}
      <BottomNav cartCount={cartCount} />
    </div>
  );
}

export default WWGStorePage;
